import { ReactComponent as ArrowDownIcon } from '@app/assets/icons/arrow.svg';
import { localizeDayOfWeek } from '@app/i18n';
import cx from 'classnames';
import {
  addDays,
  addMonths,
  addWeeks,
  Day,
  endOfMonth,
  format,
  Interval as Range,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isSameWeek,
  isToday,
  isWithinInterval,
  setDefaultOptions,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns';
import times from 'lodash/times';
import { FC, useCallback, useLayoutEffect, useMemo, useState } from 'react';

import './calendar.scss';
import '@app/index.scss';

setDefaultOptions({ weekStartsOn: 1 });

type Week = Date[];

export enum CalendarView {
  MONTH = 'month',
  WEEK = 'week',
}

export type CalendarProps = {
  className?: string;
  headClassName?: string;
  bodyClassName?: string;
  daysOfWeekClassName?: string;
  dayOfWeekClassName?: string;
  daysClassName?: string;
  dayClassName?: string;
  activeClassName?: string;
  todayClassName?: string;
  disabledClassName?: string;
  inRangeClassName?: string;

  view?: CalendarView;
  minDate?: Date;
  maxDate?: Date;
  unavailableDates?: Date[];
  rangeMode?: boolean;

  preselectedDate?: Date;
  preselectedRange?: Range;
  onDateSelected?: (date: Date) => void;
  onRangeSelected?: (range: Range) => void;
};

const DAYS_IN_WEEK = 7;

const weeksToView = {
  [CalendarView.MONTH]: 6,
  [CalendarView.WEEK]: 1,
};

const startOfFn = {
  [CalendarView.MONTH]: startOfMonth,
  [CalendarView.WEEK]: startOfWeek,
};

const addFn = {
  [CalendarView.MONTH]: addMonths,
  [CalendarView.WEEK]: addWeeks,
};

const subFn = {
  [CalendarView.MONTH]: subMonths,
  [CalendarView.WEEK]: subWeeks,
};

const isSamePeriodFn = {
  [CalendarView.MONTH]: isSameMonth,
  [CalendarView.WEEK]: isSameWeek,
};

export const Calendar: FC<CalendarProps> = ({
  className,
  headClassName,
  bodyClassName,
  daysOfWeekClassName,
  dayOfWeekClassName,
  daysClassName,
  dayClassName,
  activeClassName,
  todayClassName,
  disabledClassName,
  inRangeClassName,
  view = CalendarView.MONTH,
  minDate = subDays(new Date(), 1),
  maxDate = addMonths(new Date(), 12),
  unavailableDates = [],
  preselectedDate,
  onDateSelected,
  rangeMode = false,
  preselectedRange,
  onRangeSelected,
}) => {
  const [activeDate, setActiveDate] = useState(preselectedDate);
  const [activeRange, setActiveRange] = useState<Partial<Range> | undefined>(preselectedRange);
  const [viewDate, setViewDate] = useState(preselectedDate || new Date());
  const [viewDateWeeks, setViewDateWeeks] = useState<Week[]>();

  const updateViewDateWeeks = useCallback(() => {
    setViewDateWeeks(
      times(weeksToView[view], (weekIdx: number) => {
        const monday = startOfWeek(addWeeks(startOfFn[view](viewDate), weekIdx));
        return Array<Date>(DAYS_IN_WEEK)
          .fill(monday)
          .map((date: Date, dayIdx: number) => addDays(date, dayIdx));
      }),
    );
  }, [viewDate, view]);

  const localizedViewDate = useMemo(() => format(viewDate, 'LLLL yyyy'), [viewDate]);

  const getLocalizedDayOfWeek = useCallback((day: Day): string => localizeDayOfWeek(day, { width: 'short' }), []);

  const isDateValid = useCallback(
    (date: Date): boolean =>
      minDate <= date &&
      date <= maxDate &&
      unavailableDates.every((unavailableDate) => !isSameDay(unavailableDate, date)),
    [minDate, maxDate, unavailableDates],
  );

  const onDayClick = (date: Date): void => {
    if (
      !isSamePeriodFn[view](date, viewDate) ||
      !isDateValid(date) ||
      (!rangeMode && activeDate && isSameDay(date, activeDate))
    ) {
      return;
    }
    setActiveDate(date);
    onDateSelected?.(date);
    if (!rangeMode) {
      return;
    }
    if (activeRange?.start && activeRange?.end) {
      if (
        isBefore(date, activeRange.start) ||
        isSameDay(date, activeRange.start) ||
        isSameDay(date, activeRange.end) ||
        isAfter(date, activeRange.end)
      ) {
        setActiveRange({
          start: date,
        });
      } else if (isWithinInterval(date, activeRange as Range)) {
        const newRange = {
          start: date,
          end: activeRange.end,
        };
        setActiveRange(newRange);
        onRangeSelected?.(newRange);
      }
    } else if (activeRange?.start && !activeRange?.end) {
      if (isAfter(date, activeRange.start)) {
        const newRange = {
          start: activeRange.start,
          end: date,
        };
        setActiveRange(newRange);
        onRangeSelected?.(newRange);
      } else {
        setActiveRange({
          start: date,
        });
      }
    } else if (!activeRange?.start && !activeRange?.end) {
      if ((activeDate && (isBefore(date, activeDate) || isSameDay(date, activeDate))) || !activeDate) {
        setActiveRange({
          start: date,
        });
      } else if (activeDate && isAfter(date, activeDate)) {
        const newRange = {
          start: activeDate,
          end: date,
        };
        setActiveRange(newRange);
        onRangeSelected?.(newRange);
      }
    }
  };

  const returnToCurrentPeriod = useCallback(() => {
    setViewDate(new Date());
    updateViewDateWeeks();
  }, [updateViewDateWeeks]);

  const setNextPeriod = useCallback(() => {
    const nextPeriod: Date = addFn[view](viewDate, 1);
    if (startOfMonth(nextPeriod) < maxDate) {
      setViewDate(nextPeriod);
      updateViewDateWeeks();
    }
  }, [view, viewDate, maxDate, updateViewDateWeeks]);

  const setPrevPeriod = useCallback(() => {
    const prevPeriod = subFn[view](viewDate, 1);
    if (minDate < endOfMonth(prevPeriod)) {
      setViewDate(prevPeriod);
      updateViewDateWeeks();
    }
  }, [view, viewDate, minDate, updateViewDateWeeks]);

  const localizedDayOfWeekNodes = useMemo(() => {
    const localizedDays = [];
    for (let dayIdx: Day = 1; dayIdx < 7; ++dayIdx) {
      localizedDays.push(getLocalizedDayOfWeek(dayIdx as Day));
    }
    localizedDays.push(getLocalizedDayOfWeek(0 as Day));
    return localizedDays.map((day) => (
      <th className={cx(dayOfWeekClassName, 'calendar__day-of-week')} key={day}>
        <time>{day}</time>
      </th>
    ));
  }, [getLocalizedDayOfWeek, dayOfWeekClassName]);

  const dayCellClasses = (day: Date): string =>
    cx('calendar__day-cell', dayClassName, {
      [cx(disabledClassName, 'calendar__day-cell--disabled')]:
        !isSamePeriodFn[view](day, viewDate) || !isDateValid(day),
      [cx(activeClassName, 'calendar__day-cell--active')]:
        (activeDate && isSameDay(activeDate, day)) ||
        (activeRange?.start && isSameDay(activeRange.start, day)) ||
        (activeRange?.end && isSameDay(activeRange.end, day)),
      [cx(todayClassName, 'calendar__day-cell--today')]: isToday(day),
      [cx(inRangeClassName, 'calendar__day-cell--in-range')]:
        activeRange?.start && activeRange?.end && isWithinInterval(day, activeRange as Range),
      'calendar__day-cell--active-start': activeRange?.start && activeRange?.end && isSameDay(activeRange.start, day),
      'calendar__day-cell--active-end': activeRange?.end && isSameDay(activeRange.end, day),
    });

  useLayoutEffect(updateViewDateWeeks, [updateViewDateWeeks]);

  return (
    <div className={cx(className, 'calendar')}>
      <nav className={cx(headClassName, 'calendar__head')}>
        <button type="button" aria-label="Предыдущий период" className="calendar__prev-btn" onClick={setPrevPeriod}>
          <ArrowDownIcon className="calendar__nav-icon" style={{ rotate: '90deg' }} />
        </button>
        <button
          type="button"
          aria-label="Вернуться на текущий период"
          className="calendar__current-month-btn"
          onClick={returnToCurrentPeriod}
        >
          <time dateTime={format(viewDate, 'yyyy-MM')}>{localizedViewDate}</time>
        </button>
        <button type="button" aria-label="Следующий период" className="calendar__next-btn" onClick={setNextPeriod}>
          <ArrowDownIcon className="calendar__nav-icon" style={{ rotate: '-90deg' }} />
        </button>
      </nav>

      <table className={cx(bodyClassName, 'calendar__body')}>
        <thead className={daysOfWeekClassName}>
          <tr>{localizedDayOfWeekNodes}</tr>
        </thead>
        <tbody className={daysClassName}>
          {viewDateWeeks?.map((week) => (
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            <tr key={week[0].toISOString() + week.at(-1)!.toISOString()}>
              {week.map((day) => (
                <td key={day.toISOString()} className={dayCellClasses(day)}>
                  <button className="calendar__day-cell-btn" type="button" onClick={() => onDayClick(day)}>
                    <time dateTime={format(day, 'yyyy-MM-dd')}>{day.getDate()}</time>
                  </button>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
